import React from 'react';
import { MuiDrawer, MuiCard } from '@ace/ace-ui-web';
import { Gauge } from '@mui/x-charts/Gauge';
import { getPerformanceAnalysis } from '../../utils/performanceUtils';
import styles from './KpiDrawer.module.css';

// Module-level constants
const DRAWER_WIDTH_PX = 600;
const GAUGE_SIZE = 120;
const GAUGE_INNER_RADIUS = '75%';
const GAUGE_OUTER_RADIUS = '100%';

/**
 * Props interface for the KpiDrawer component
 * @interface KpiDrawerProps
 */
interface KpiDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Function called when drawer should be closed */
  onClose: () => void;
  /** Array of KPI data to display in tabs */
  tabs: Array<{
    /** Display label for the tab */
    label: string;
    /** Current KPI value */
    current: number;
    /** Target KPI value */
    target: number;
    /** Color for the KPI gauge */
    kpiColor: string;
    /** True when the API returned goal data; false means no data — show '-' instead of 0/1 */
    hasData: boolean;
  }>;
}

/**
 * KpiDrawer component displays KPI performance data in a drawer format
 * with gauge visualizations and performance analysis.
 *
 * @param props - The component props
 * @returns JSX element representing the KPI drawer
 */
const KpiDrawer: React.FC<KpiDrawerProps> = ({ open, onClose, tabs }) => {
  const renderKpiDrawerContent = () => (
    <div className={styles.kpiDrawerContent}>
      {tabs.map((tab, index) => {
        const performanceData = getPerformanceAnalysis(tab.label);
        return (
          <div
            key={index}
            className={styles.kpiCard}>
            <MuiCard
              borderRadius={2}
              elevation={1}
              padding={3}
              variant="elevation">
              <div className={styles.kpiCardHeader}>
                <div className={styles.gaugeContainer}>
                  <h2 className={styles.kpiTitle}>{tab.label}</h2>
                  <Gauge
                    value={tab.current}
                    valueMax={tab.target}
                    width={GAUGE_SIZE}
                    height={GAUGE_SIZE}
                    innerRadius={GAUGE_INNER_RADIUS}
                    outerRadius={GAUGE_OUTER_RADIUS}
                    sx={{
                      '& .MuiGauge-valueArc': {
                        fill: tab.kpiColor,
                      },
                      '& .MuiGauge-referenceArc': {
                        fill: '#E0E0E0',
                      },
                      '& .MuiGauge-valueText': {
                        fontSize: '18px',
                        fontWeight: '600',
                      },
                    }}
                    text={({ value, valueMax }) => (tab.hasData ? `${value}/${valueMax}` : '-')}
                  />
                </div>
                <div className={styles.kpiInfo}>
                  <div className={styles.analysisSection}>
                    <h4 className={styles.analysisTitle}>Performance Analysis</h4>
                    <p className={styles.analysisText}>{performanceData.analysis}</p>
                  </div>

                  <div>
                    <h4 className={styles.recommendationsTitle}>Improvement Recommendations</h4>
                    <p className={styles.recommendationsText}>{performanceData.recommendations}</p>
                  </div>
                </div>
              </div>
            </MuiCard>
          </div>
        );
      })}
    </div>
  );

  return (
    <MuiDrawer
      open={open}
      onClose={onClose}
      anchor="right"
      headerText="Key Performance Indicators (KPIs)"
      subHeaderText="KPIs are quantifiable measurements that evaluate how effectively your store achieves key business objectives. These metrics help track performance across critical areas and identify opportunities for improvement."
      footerButtonText="Close"
      autoWidth={false}
      width={DRAWER_WIDTH_PX}>
      {renderKpiDrawerContent()}
    </MuiDrawer>
  );
};

export default KpiDrawer;
